import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   * Also handles showing/hiding the delete icon when quantity is 1
   */
  updateButtonStates() {
    const { minusButton, plusButton, minusIcon, deleteIcon } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart minus button is never disabled - at quantity 1 it shows delete icon
    minusButton.disabled = false;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;

    // Toggle between minus icon and delete icon based on quantity
    if (minusIcon && deleteIcon) {
      if (value <= 1) {
        minusIcon.classList.add('hidden');
        deleteIcon.classList.remove('hidden');
      } else {
        minusIcon.classList.remove('hidden');
        deleteIcon.classList.add('hidden');
      }
    }
  }

  /**
   * Override decrease to allow going to 0 (remove item) when at quantity 1
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { quantityInput } = this.refs;
    const { step, value } = this.getCurrentValues();

    // Allow decreasing to 0 to remove the item
    const newValue = Math.max(0, value - step);

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
